import type {
  DocumentCreateRequest,
  DocumentFolderResponse,
  DocumentMoveRequest,
  DocumentResponse,
  DocumentUpdateRequest,
  GetDocumentHistoryRequestV2,
  GetDocumentListRequest,
  GetDocumentTreeRequest,
  GetListResponseOfDocumentResponse,
  TreeResponse,
} from 'src/api/ProactApiClient';
import {documentApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery, voidClientQuery} from '../clientQuery';

/**
 * Writing a document can open, supersede or orphan a review round and the Approval /
 * ToolboxTalk task that carries it, so every document mutation has to invalidate the
 * review and task caches alongside the document itself. The round is created by
 * DocumentReviewRequestedJob, so the refetch may land before the round exists — the
 * review surfaces refetch again when the task tags are invalidated by the job's
 * downstream transitions.
 */
const documentMutationTags = [
  'Documents',
  'DocumentReviewRequests',
  'TaskDefinitions',
  'TaskAssignments',
] as const;

/**
 * A document is identified by id + version. Omitting the version returns the
 * highest-numbered version, which is not necessarily the active one — a review-gated
 * update leaves the approved row active while the newer row awaits approval.
 */
export interface GetDocumentRequest {
  id: number;
  version?: number;
}

export const documentApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getDocument: builder.query<DocumentResponse, GetDocumentRequest>({
      queryFn: clientQuery(({id, version}) =>
        documentApiClient.get(id, version)
      ),
      providesTags: ['Documents'],
    }),

    getDocumentList: builder.query<
      DocumentFolderResponse,
      GetDocumentListRequest
    >({
      queryFn: clientQuery(params => documentApiClient.list(params)),
      providesTags: ['Documents'],
    }),
    getDocumentTree: builder.query<TreeResponse[], GetDocumentTreeRequest>({
      queryFn: clientQuery(params => documentApiClient.tree(params)),
      providesTags: ['Documents'],
    }),
    getDocumentHistoryListV2: builder.query<
      GetListResponseOfDocumentResponse,
      GetDocumentHistoryRequestV2
    >({
      queryFn: clientQuery(params =>
        documentApiClient.getDocumentHistoryV2(params)
      ),
      providesTags: ['Documents'],
    }),
    createDocumentNode: builder.mutation<
      DocumentResponse,
      DocumentCreateRequest
    >({
      queryFn: clientQuery(request =>
        documentApiClient.createNewDocumentNode(request)
      ),
      invalidatesTags: [...documentMutationTags],
    }),
    updateDocument: builder.mutation<DocumentResponse, DocumentUpdateRequest>({
      queryFn: clientQuery(request => documentApiClient.update(request)),
      invalidatesTags: [...documentMutationTags],
    }),
    deleteDocument: builder.mutation<void, number>({
      queryFn: voidClientQuery(id => documentApiClient.delete(id)),
      invalidatesTags: [...documentMutationTags],
    }),
    moveDocument: builder.mutation<DocumentResponse, DocumentMoveRequest>({
      queryFn: clientQuery(request => documentApiClient.move(request)),
      invalidatesTags: [...documentMutationTags],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDocumentQuery,
  useGetDocumentListQuery,
  useGetDocumentTreeQuery,
  useGetDocumentHistoryListV2Query,
  useCreateDocumentNodeMutation,
  useUpdateDocumentMutation,
  useDeleteDocumentMutation,
  useMoveDocumentMutation,
} = documentApi;
